import React, { useRef, useEffect, useState } from "react";
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, Underline, Heading1, Heading2, ImagePlus } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";
import { api } from "@/lib/auth";
import { toast } from "sonner";

// Minimal contenteditable WYSIWYG. Stores HTML in `value`.
export default function RichTextEditor({ value, onChange, placeholder = "Write here…" }) {
  const ref = useRef();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const clean = sanitizeHtml(value || "");
    if (ref.current && ref.current.innerHTML !== clean) {
      ref.current.innerHTML = clean;
    }
  }, [value]);

  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(ref.current.innerHTML);
  };

  const link = () => {
    const url = window.prompt("Enter URL (must start with https://):");
    if (!url) return;
    if (!url.startsWith("https://") && !url.startsWith("http://") && !url.startsWith("mailto:")) {
      alert("URL must start with https:// or http:// or mailto:"); return;
    }
    exec("createLink", url);
  };

  const insertPhotos = async (files) => {
    const list = Array.from(files || []).filter(f => f.type.startsWith("image/"));
    if (!list.length) return;
    setUploading(true);
    try {
      let html = "";
      for (const f of list) {
        const form = new FormData(); form.append("file", f);
        const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        html += `<figure><img src="${data.url}" alt="" /><figcaption>Add a caption…</figcaption></figure><p><br></p>`;
      }
      ref.current?.focus();
      if (!document.execCommand("insertHTML", false, html)) ref.current.innerHTML += html;
      onChange(ref.current.innerHTML);
      toast.success(`${list.length} photo${list.length > 1 ? "s" : ""} added`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Photo upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const btn = (Icon, cmd, arg, label) => (
    <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec(cmd, arg)}
      className="p-2 hover:bg-white/10 text-zinc-300" title={label} data-testid={`rte-${cmd}`}>
      <Icon size={14}/>
    </button>
  );

  return (
    <div className="border border-white/10 bg-black">
      <div className="flex flex-wrap gap-1 border-b border-white/10 p-1 bg-[#0a0a0a]">
        {btn(Bold, "bold", null, "Bold")}
        {btn(Italic, "italic", null, "Italic")}
        {btn(Underline, "underline", null, "Underline")}
        {btn(Heading1, "formatBlock", "H2", "Heading")}
        {btn(Heading2, "formatBlock", "H3", "Sub-heading")}
        {btn(List, "insertUnorderedList", null, "Bullet list")}
        {btn(ListOrdered, "insertOrderedList", null, "Numbered list")}
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={link}
          className="p-2 hover:bg-white/10 text-zinc-300" title="Link" data-testid="rte-link">
          <LinkIcon size={14}/>
        </button>
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => fileRef.current?.click()} disabled={uploading}
          className="p-2 hover:bg-white/10 text-zinc-300 disabled:opacity-50 flex items-center gap-1 text-xs" title="Insert photos" data-testid="rte-photo">
          <ImagePlus size={14}/>{uploading ? "Uploading…" : "Photos"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e => insertPhotos(e.target.files)} data-testid="rte-photo-input"/>
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec("removeFormat")}
          className="ml-auto px-3 text-xs uppercase tracking-widest text-zinc-500 hover:text-white">Clear</button>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning
        onInput={e => onChange(e.currentTarget.innerHTML)}
        onDrop={e => { if (e.dataTransfer?.files?.length) { e.preventDefault(); insertPhotos(e.dataTransfer.files); } }}
        onPaste={e => { const imgs = Array.from(e.clipboardData?.files || []).filter(f => f.type.startsWith("image/")); if (imgs.length) { e.preventDefault(); insertPhotos(imgs); } }}
        className="min-h-[200px] p-4 text-sm text-zinc-200 focus:outline-none prose-invert rich-content"
        style={{ lineHeight: 1.6 }}
        data-placeholder={placeholder}/>
    </div>
  );
}
